import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, IconButton, CircularProgress, Modal, TextField
} from "@mui/material";
import { ArrowBack, Download, Add, Edit, Delete } from "@mui/icons-material";
import FolderIcon from "@mui/icons-material/Folder";
import { FolderDocument, Document } from "../../../types/folderTypes";
import Layout from "../../../components/layouts/Layout";
import { toast } from "react-toastify";
import DragAndDropUploadWithFolder from "../../../components/DragAndDropUploadWithFolder";
import ConfirmDialog from "../../../components/dialog/ConfirmationDialog";
import UserFolderService from "../../../service/user/folderService";
import FileViewerModal from "../../../components/folderRepo/FileViewerModal";


const FolderDetail = () => {
    const navigate = useNavigate();
    const [viewerDoc, setViewerDoc] = useState<Document | null>(null);
    const { id: folderId } = useParams();

    const [subfolders, setSubfolders] = useState<FolderDocument[]>([]);
    const [files, setFiles] = useState<Document[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
    const [newFolderName, setNewFolderName] = useState<string>("");
    const [editingFolder, setEditingFolder] = useState<FolderDocument | null>(null);

    const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
    const [folderToDelete, setFolderToDelete] = useState<number | null>(null);

    const [deleteFileComfirmOpen, setDeleteFileComfirmOpen] = useState(false);
    const [fileToDelete, setFileToDelete] = useState<string | null>(null);

    const [fileModalOpen, setFileModalOpen] = useState<boolean>(false);
    const [fileModalTitle, setFileModalTitle] = useState<string>("");
    const [editingName, setEditingName] = useState<string>("");
    const [editingItem, setEditingItem] = useState<Document | null>(null);
    const [isEditingFolder, setIsEditingFolder] = useState<boolean>(false);

    const [breadcrumbs, setBreadcrumbs] = useState<{ id?: number, name: string, isRepo?: boolean }[]>([]);

    useEffect(() => {
        if (folderId) {
            fetchFolderDetails();
        }
    }, [folderId]);

    const fetchFolderDetails = async () => {
        try {
            setLoading(true);
            const response = await UserFolderService.getFolderDetail(folderId as string);

            if (response.breadcrumb) {
                setBreadcrumbs([
                    { name: "Space", isRepo: true },
                    ...response.breadcrumb
                ]);
            } else {
                setBreadcrumbs([]);
            }
            setSubfolders(response.folders || []);
            setFiles(response.documents || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) {
            toast.error("Folder name cannot be empty.");
            return;
        }

        try {
            await UserFolderService.addSubFolder({
                folder_name: newFolderName,
                parent_folder_id: folderId!,
            });

            toast.success("Folder created successfully!");
            setModalOpen(false);
            setNewFolderName("");
            fetchFolderDetails();
        } catch (error) {
            toast.error("Failed to create folder.");
        }
    };

    const handleEditFolder = async () => {
        if (!editingFolder || !newFolderName.trim()) {
            toast.error("Folder name cannot be empty.");
            return;
        }

        try {
            const cleanedFolderName = newFolderName.replace(/\.[^/.]+$/, "");

            await UserFolderService.updateSubFolder({
                folder_name: cleanedFolderName,
                spaceFolderId: editingFolder.id,
            });

            toast.success("Folder updated successfully!");
            fetchFolderDetails();
            setEditModalOpen(false);
            setEditingFolder(null);
            setNewFolderName("");
        } catch (error) {
            toast.error("Failed to update folder.");
        }
    };


    const handleDeleteFolder = async () => {
        if (!folderToDelete) return;
        try {
            await UserFolderService.removeSubFolder(folderToDelete);
            toast.success("Folder deleted successfully!");
            setConfirmOpen(false);
            fetchFolderDetails();
        } catch (error) {
            toast.error("Failed to delete folder.");
        }
    };

    const handleDownloadFolder = async (folder: any) => {
        try {
            await UserFolderService.downloadSingleFolder(folder);
            toast.success("Folder downloaded.");
        } catch (error: any) {
            toast.error(error.message || "Failed to download folder.");
        }
    };


    const handleFolderClick = (subfolder: FolderDocument) => {
        navigate(`/ca/folder/${subfolder.id}`);
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center py-6">
                    <CircularProgress />
                </div>
            </Layout>
        );
    }

    const handleDownloadFile = async (documentId: string, documentName: string) => {
        try {
            await UserFolderService.downloadSingleFile(documentId, documentName);
            toast.success(`${documentName} downloaded.`);
        } catch (error: any) {
            toast.error(error.message || "Failed to download folder.");
        }
    };

    const handleEditDocument = (document: Document) => {
        setIsEditingFolder(false);
        console.log(document.document_name);
        const nameWithoutExtension = document.document_name.replace(/\.[^/.]+$/, "");

        setEditingName(nameWithoutExtension);
        setEditingItem(document);
        setFileModalTitle("Edit Document: " + document.document_name);
        setFileModalOpen(true);
    };

    const confirmDeletefile = (fileId: string) => {
        setFileToDelete(fileId);
        setDeleteFileComfirmOpen(true);
    };

    const handleDeleteFile = async () => {
        if (!fileToDelete) return;
        try {
            await UserFolderService.deleteFile(fileToDelete);
            toast.success("File deleted successfully!");
            fetchFolderDetails();
        } catch (error) {
            toast.error("Failed to delete file.");
        } finally {
            setDeleteFileComfirmOpen(false);
            setFileToDelete(null);
        }
    };

    const handleSaveEdit = async () => {
        if (!editingItem) return;

        try {
            const document = editingItem as Document;

            // Extract file extension from original name
            const fileExtension = document.document_name.split('.').pop();
            const newFileName = `${editingName}.${fileExtension}`; // Keep original extension

            await UserFolderService.updateFileName(document.id.toString(), newFileName);
            toast.success("Document updated successfully!");
            toast.success("Document updated successfully!");
            fetchFolderDetails();
            setFileModalOpen(false);
            setEditingItem(null);
            setEditingName("");
        } catch (error) {
            toast.error("Failed to save changes.");
        }
    };

    const handleDownloadZipFolder = async () => {
        try {
            const folderName = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].name : "Folder";

            await UserFolderService.downloadSingleFolder({
                id: folderId,
                folder_name: folderName
            });

            toast.success(`${folderName} downloaded successfully!`);
        } catch (error) {
            toast.error("Failed to download the folder.");
        }
    };


    return (
        <Layout>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                    <IconButton onClick={() => navigate(-1)}>
                        <ArrowBack />
                    </IconButton>
                    {breadcrumbs.length > 0 && breadcrumbs.map((item, index) => (
                        <span key={index} className="inline-flex">
                            <span
                                className={`cursor-pointer ${item.isRepo ? "text-green-600" : "text-[#632DE9]"} hover:underline`}
                                onClick={() => {
                                    if (item.isRepo) {
                                        navigate(`/ca/folderRepo`);
                                    } else {
                                        navigate(`/ca/folder/${item.id}`);
                                    }
                                }}
                            >
                                {item.name}
                            </span>
                            {index < breadcrumbs.length - 1 && <span className="mx-1"> &gt; </span>}
                        </span>
                    ))}
                </div>

                {/* Right side with Action Buttons */}
                <div className="flex gap-2">
                    <button
                        className="bg-[#632DE9] text-white font-medium px-4 py-2 rounded-lg flex items-center gap-2"
                        onClick={() => setModalOpen(true)}
                    >
                        <Add className="text-white" /> Create Folder
                    </button>
                    {
                        (subfolders.length > 0 || files.length > 0) && (
                            <button
                                className="bg-green-500 hover:bg-green-600 text-white font-medium px-4 py-2 rounded-lg flex items-center gap-2"
                                onClick={() => handleDownloadZipFolder()}
                            >
                                <Download className="text-white" /> Download Folder
                            </button>
                        )
                    }
                </div>
            </div>

            {
                (files.length == 0 && subfolders.length == 0) ? (
                    <div className="text-center text-gray-500 py-10">
                        <p>No files or folders found.</p>
                        <p className="mt-2">Create a new folder or upload files to get started.</p>
                    </div>
                ) : (
                    <>
                        <div className="overflow-hidden rounded-lg border border-gray-200">
                            <TableContainer className="bg-white">
                                <Table>
                                    <TableHead className="bg-gray-100">
                                        <TableRow>
                                            <TableCell className="font-semibold">#</TableCell>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Created At</TableCell>
                                            <TableCell>Last Modified By</TableCell>
                                            <TableCell>Last Modified At</TableCell>
                                            <TableCell>Download</TableCell>
                                            <TableCell>Edit</TableCell>
                                            <TableCell>Delete</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>

                                        {/* Display Subfolders First */}
                                        {subfolders.length > 0 && (
                                            subfolders.map((subfolder, index) => {
                                                const lastLog = subfolder.activity_logs?.[0];
                                                return (
                                                    <TableRow key={subfolder.id}>
                                                        <TableCell>{index + 1}</TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center">
                                                                <FolderIcon className="text-[#632DE9] mr-2" />
                                                                <span
                                                                    className="font-medium text-[#632DE9] cursor-pointer hover:underline"
                                                                    onClick={() => handleFolderClick(subfolder)}
                                                                >
                                                                    {subfolder.folder_name}
                                                                </span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>Folder</TableCell>
                                                        <TableCell>{new Date(subfolder.created_at).toLocaleString()}</TableCell>
                                                        <TableCell>{lastLog?.user_name ?? "—"}</TableCell>
                                                        <TableCell>{lastLog?.updated_at ? new Date(lastLog.updated_at).toLocaleString() : "—"}</TableCell>
                                                        <TableCell>
                                                            <IconButton color="primary" onClick={() => handleDownloadFolder(subfolder)}>
                                                                <Download />
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell>
                                                            <IconButton color="primary" onClick={() => {
                                                                setEditingFolder(subfolder);
                                                                setNewFolderName(subfolder.folder_name);
                                                                setEditModalOpen(true);
                                                            }}>
                                                                <Edit />
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell>
                                                            <IconButton color="error" onClick={() => {
                                                                setFolderToDelete(subfolder.id);
                                                                setConfirmOpen(true);
                                                            }}>
                                                                <Delete />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}

                                        {/* Display Files Under Parent Folder */}
                                        {files.length > 0 && (
                                            files.map((doc: Document, index) => {
                                                const lastLog = doc.activity_logs?.[0];
                                                return (
                                                    <TableRow key={doc.id} className="border-b">
                                                        <TableCell>{subfolders.length + index + 1}</TableCell>
                                                        <TableCell>
                                                            <span
                                                                className="text-[#632DE9] hover:underline font-medium cursor-pointer"
                                                                onClick={() => setViewerDoc(doc)}
                                                            >
                                                                {doc.document_name}
                                                            </span>
                                                        </TableCell>
                                                        <TableCell>File</TableCell>
                                                        <TableCell>{new Date(doc.created_at).toLocaleString()}</TableCell>
                                                        <TableCell>{lastLog?.user_name ?? "—"}</TableCell>
                                                        <TableCell>{lastLog?.updated_at ? new Date(lastLog.updated_at).toLocaleString() : "—"}</TableCell>
                                                        <TableCell>
                                                            <IconButton color="primary" onClick={() => handleDownloadFile(doc.id.toString(), doc.document_name)}>
                                                                <Download />
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell>
                                                            <IconButton color="primary" size="small" onClick={() => handleEditDocument(doc)}>
                                                                <Edit />
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell>
                                                            <IconButton color="error" size="small" onClick={() => confirmDeletefile(doc.id.toString())}>
                                                                <Delete />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    </>
                )
            }

            <div className="mt-6">
                <DragAndDropUploadWithFolder spaceId={'0'} onUploadSuccess={fetchFolderDetails} selectedFolder={Number(folderId)} />
            </div>

            <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                <div className="bg-white p-6 rounded-lg shadow-lg mx-auto w-1/3 mt-20">
                    <h2 className="text-lg font-semibold mb-4">Create New Folder</h2>
                    <TextField
                        fullWidth
                        label="Folder Name"
                        variant="outlined"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && newFolderName.trim() && handleCreateFolder()}
                        className="mb-4"
                    />
                    <div className="flex justify-end mt-4">
                        <Button onClick={() => setModalOpen(false)} className="mr-2">Cancel</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCreateFolder}
                            disabled={!newFolderName.trim()}>
                            Create
                        </Button>
                    </div>
                </div>
            </Modal>

            <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
                <div className="bg-white p-6 rounded-lg shadow-lg mx-auto w-1/3 mt-20">
                    <h2 className="text-lg font-semibold mb-4">
                        Edit Folder: {editingFolder?.folder_name || "Untitled"}
                    </h2>

                    <TextField
                        fullWidth
                        variant="outlined"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        className="mb-4"
                        onKeyDown={(e) => e.key === "Enter" && newFolderName.trim() && handleEditFolder()}
                    />
                    <Button variant="contained" color="primary" onClick={handleEditFolder} disabled={!newFolderName.trim()}>
                        Save Changes
                    </Button>
                </div>
            </Modal>

            <Modal open={fileModalOpen} onClose={() => setFileModalOpen(false)}>
                <div className="bg-white p-6 rounded-lg shadow-lg mx-auto w-1/3 mt-20">
                    <h2 className="text-lg font-semibold mb-4">{fileModalTitle}</h2>
                    <TextField
                        fullWidth
                        variant="outlined"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && editingName.trim() && handleSaveEdit()}
                    />
                    <div className="flex justify-end mt-4">
                        <Button onClick={() => setFileModalOpen(false)}>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={handleSaveEdit} disabled={!editingName.trim()}>Save</Button>
                    </div>
                </div>
            </Modal>


            <ConfirmDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleDeleteFolder}
                title="Delete Folder"
                message="Are you sure you want to delete this folder? This action cannot be undone."
            />

            <ConfirmDialog
                open={deleteFileComfirmOpen}
                onClose={() => setDeleteFileComfirmOpen(false)}
                onConfirm={handleDeleteFile}
                title="Delete File"
                message="Are you sure you want to delete this file? This action cannot be undone."
            />

            <FileViewerModal document={viewerDoc} onClose={() => setViewerDoc(null)} />
        </Layout>
    );
};

export default FolderDetail;
